import { apiFetch } from "./client.js";

export interface DocumentListItem {
  id: number;
  slug: string;
  title: string;
  sectionId: string | null;
  sectionTitle: string | null;
  sortOrder: number;
  classification: "öffentlich" | "intern" | "vertraulich";
  ersteller: string | null;
  version: string | null;
  freigabe: string | null;
  /** Freigabedatum of the current version, ISO YYYY-MM-DD, or null if unrecorded. */
  versionDate: string | null;
  published: boolean;
}

export interface DocsBundleState {
  slug: string;
  zipPath: string | null;
  zipMtime: number | null;
  unpackedMtime: number | null;
  /** True when this instance's last unpack attempt failed or has not run since a newer zip appeared. */
  stale: boolean;
  /** The error message from a failed unpack attempt, or null. */
  error: string | null;
  pageCount: number;
}

export interface DocsDiagnostics {
  docsDirConfigured: boolean;
  lastIndexedAt: number | null;
  defaultClassified: string[];
  publishedMismatch: string[];
  /** Present documents published (in the öffentlich tree) despite a higher classification — a security-relevant misconfiguration. */
  publishedAboveClearance: string[];
  missing: string[];
  /** Documents whose footer version is older than their own Änderungshistorie records. */
  versionBehindHistory: string[];
  /** Documents carrying a version with no matching dated history row. */
  versionUndated: string[];
  /** English topics in the English trees pairing to no German document. */
  unpairedTranslations: string[];
  /** Documents whose English footer declares a different classification than their German original. */
  translationClassificationMismatch: string[];
  bundles: DocsBundleState[];
  /** True when a source has been edited more recently than the oldest built bundle — the register may show metadata newer than the served pages. */
  sourcesNewerThanBundles: boolean;
}

export interface DocumentTranslation {
  available: boolean;
  /** The locale actually rendered: "de" when en was requested but absent. */
  rendered: "de" | "en";
  /** Non-null only when the translation was made against an older German version. */
  stale: { translated: string | null; original: string | null } | null;
  /** The English topic slug this document renders as, or null when no English rendering exists for it. */
  slug: string | null;
}

export interface DocumentDetail {
  id: number;
  slug: string;
  title: string;
  sectionTitle: string | null;
  classification: "öffentlich" | "intern" | "vertraulich";
  ersteller: string | null;
  version: string | null;
  freigabe: string | null;
  /** Freigabedatum of the current version, ISO YYYY-MM-DD, or null if unrecorded. */
  versionDate: string | null;
  published: boolean;
  /** URL of the page inside the served bundle, or null when not yet built. */
  bundleUrl: string | null;
  translation: DocumentTranslation;
}

export interface SearchHit {
  slug: string;
  title: string;
  snippet: string;
}

export const docsApi = {
  // German sends no parameter at all, so the request URL stays byte-identical
  // to before locale support existed.
  list: (locale?: "de" | "en") =>
    apiFetch<{ items: DocumentListItem[] }>(`/api/docs${locale === "en" ? "?locale=en" : ""}`),
  diagnostics: () => apiFetch<DocsDiagnostics>("/api/docs/diagnostics"),
  reindex: () => apiFetch<{ indexed: number; missing: number }>("/api/docs/reindex", { method: "POST" }),
  // German sends no parameter at all, so the request URL stays byte-identical
  // to before locale support existed.
  item: (slug: string, locale?: "de" | "en") =>
    apiFetch<DocumentDetail>(
      `/api/docs/item/${encodeURIComponent(slug)}${locale === "en" ? "?locale=en" : ""}`,
    ),
  search: (q: string, locale?: "de" | "en") =>
    apiFetch<{ items: SearchHit[] }>(
      `/api/docs/search?q=${encodeURIComponent(q)}${locale === "en" ? "&locale=en" : ""}`,
    ),
};
